import { Box, TextButton } from '@wix/design-system'
import { useContent, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { tryCatch } from '@wix/funnel-utils'
import React, { FC } from 'react'
import { useSearchParams } from 'react-router-dom'

import { markAsWixStudioAccount } from '../../../api/StudioWorkspaceService/markAsWixStudioAccount'
import { usePageLogger } from '../../../bi/mfi'
import { RichText } from '../../reusable/RichText'
import { WixStudioImageButtons } from '../../reusable/WixStudioImageButtons/WixStudioImageButtons'
import { WixStudioTemplate } from '../../reusable/WixStudioTemplate/WixStudioTemplate'
import { SidePanel } from './SidePanel'
import type { TWixStudioWelcome as Content } from './WixStudioWelcome.content'
import { wixStudioWelcomeDataHooks as dataHooks } from './WixStudioWelcome.dataHooks'
import s from './WixStudioWelcome.scss'

export const WixStudioWelcome: FC = () => {
  const [searchParams] = useSearchParams()
  const [isPageLoading] = usePageLoading()
  const [isButtonAlreadyClicked] = React.useState(false)
  const isClickDisabled = isPageLoading || isButtonAlreadyClicked
  const { content } = useContent<Content>()
  const questionOptionsExceptLast = content.question.options.slice(0, -1)
  const lastQuestionOption = content.question.options[content.question.options.length - 1]
  const pageLogger = usePageLogger()
  const next = useNext()
  const [hoveredIndex, setHoveredIndex] = React.useState<number>(-1)

  const authMethod = searchParams.get('authMethod')
  const isDisclaimerVisible = content.disclaimer && authMethod === 'GOOGLE_ONE_TAP'

  const handleBtnClick = (optionKey: string) => async () => {
    if (isClickDisabled) {
      return
    }

    void tryCatch(markAsWixStudioAccount) // no await to not block the transition
    await pageLogger.answer({ answer: optionKey })
    next({
      [content.question.questionKey]: { values: [optionKey] },
    })
  }

  const handleLinkClick = (optionKey: string) => async () => {
    if (isClickDisabled) {
      return
    }

    await pageLogger.answer({ answer: optionKey })
    next({
      [content.question.questionKey]: { values: [optionKey] },
    })
  }

  const hoveredOption = content.question.options[hoveredIndex]
  const hoveredOptionKey = hoveredOption ? hoveredOption.optionKey : 'none'

  const sidePanelTexts = content.question.options.map(option => ({
    text: option.sidePanelText,
    isActive: option.optionKey === hoveredOptionKey,
  }))

  return (
    <WixStudioTemplate
      isPageLoading={isPageLoading}
      pageLogger={pageLogger}
      title={
        <Box maxWidth="616px" marginBottom="30px" dataHook={dataHooks.title()}>
          {content.question.content.title}
        </Box>
      }
      subTitleAbove={content.question.content.subtitle}
      sidePanel={<SidePanel image={hoveredOptionKey} texts={sidePanelTexts} />}
      bottomNote={
        isDisclaimerVisible && (
          <Box maxWidth="582px">
            <RichText light secondary size="small" className={s.disclaimer} dataHook={dataHooks.disclaimer()}>
              {content.disclaimer}
            </RichText>
          </Box>
        )
      }
    >
      <Box maxWidth="616px" direction="vertical" marginBottom="30px">
        {questionOptionsExceptLast.map((option, index) => (
          <WixStudioImageButtons
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(-1)}
            key={option.optionKey}
            title={option.title}
            image={{
              src: option.asset.content.src,
              alt: option.asset.content.alt,
            }}
            description={option.description}
            onClick={handleBtnClick(option.optionKey)}
            dataHook={dataHooks.item({ key: option.optionKey })}
          />
        ))}
      </Box>
      <Box gap="SP1">
        <TextButton
          underline="always"
          skin="dark"
          onClick={handleLinkClick(lastQuestionOption.optionKey)}
          dataHook={dataHooks.link()}
        >
          {lastQuestionOption.title}
        </TextButton>
      </Box>
    </WixStudioTemplate>
  )
}
